export interface Vec { x: number; y: number }
export interface Rect { x: number; y: number; w: number; h: number }
export interface Seg {
  ax: number; ay: number; bx: number; by: number;
  /**
   * Optional one-sided normal. The segment only blocks rays that approach from the side the
   * normal points to, so the near and far faces of the same wall can block at different depths.
   */
  nx?: number; ny?: number;
}

export const TAU = Math.PI * 2;
export const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const dist = (ax: number, ay: number, bx: number, by: number) => Math.hypot(bx - ax, by - ay);
export const dist2 = (ax: number, ay: number, bx: number, by: number) => (bx - ax) ** 2 + (by - ay) ** 2;
export const sign = (v: number) => (v < 0 ? -1 : v > 0 ? 1 : 0);
export const approach = (v: number, target: number, step: number) =>
  v < target ? Math.min(v + step, target) : Math.max(v - step, target);

/** shortest signed angle difference a->b in (-PI, PI] */
export function angleDiff(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
export function pointInRect(px: number, py: number, r: Rect): boolean {
  return px >= r.x && py >= r.y && px < r.x + r.w && py < r.y + r.h;
}

/** Ray (ox,oy)->(dx,dy) vs segment. Returns t along ray (>=0) or null. */
export function raySegment(ox: number, oy: number, dx: number, dy: number, s: Seg): number | null {
  // one-sided: a ray running with the normal comes from behind and passes through
  if (s.nx !== undefined && dx * s.nx + dy * s.ny! >= 0) return null;
  const sx = s.bx - s.ax, sy = s.by - s.ay;
  const denom = dx * sy - dy * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const qx = s.ax - ox, qy = s.ay - oy;
  const t = (qx * sy - qy * sx) / denom;
  const u = (qx * dy - qy * dx) / denom;
  if (t >= 0 && u >= 0 && u <= 1) return t;
  return null;
}

/** Segment-segment intersection test (a->b vs seg) */
export function segmentsIntersect(ax: number, ay: number, bx: number, by: number, s: Seg): boolean {
  const dx = bx - ax, dy = by - ay;
  const t = raySegment(ax, ay, dx, dy, s);
  return t !== null && t <= 1;
}

export function pointInPolygon(px: number, py: number, poly: Float32Array | number[], n: number): boolean {
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2], yi = poly[i * 2 + 1];
    const xj = poly[j * 2], yj = poly[j * 2 + 1];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export const DIRS4 = [
  { x: 0, y: 1 }, // down
  { x: -1, y: 0 }, // left
  { x: 1, y: 0 }, // right
  { x: 0, y: -1 }, // up
] as const;

export type Facing = 'down' | 'left' | 'right' | 'up';
export function facingFromVec(x: number, y: number, prev: Facing): Facing {
  if (x === 0 && y === 0) return prev;
  if (Math.abs(x) > Math.abs(y)) return x < 0 ? 'left' : 'right';
  return y < 0 ? 'up' : 'down';
}
export function facingFromAngle(a: number): Facing {
  const c = Math.cos(a), s = Math.sin(a);
  return facingFromVec(c, s, 'down');
}
export function facingAngle(f: Facing): number {
  switch (f) {
    case 'down': return Math.PI / 2;
    case 'up': return -Math.PI / 2;
    case 'left': return Math.PI;
    case 'right': return 0;
  }
}
